import { z } from 'zod';
import { ProviderType, TargetAudience, ProviderStatus } from './provider.js';
/**
 * Result of one "Verrijk vanuit website" run for a single provider card.
 *
 * Each editable field is boxed with provenance: {value, confidence, sourceQuote,
 * sourcePageUrl}. Linda sees source quotes + URLs in the diff modal so she can
 * spot-check before clicking "Accepteer". The all-or-nothing accept UX means
 * the extractor must lean conservative — `value: null` is preferred over a
 * low-confidence guess.
 *
 * Stored only transiently in the preview response; not persisted. The apply
 * route writes the accepted values straight to the provider doc + audit row.
 */
const EnrichedString = (max) => z.object({
    value: z.string().min(1).max(max).nullable(),
    confidence: z.number().min(0).max(1),
    sourceQuote: z.string().max(2000).nullable(),
    sourcePageUrl: z.string().url().max(2000).nullable(),
});
const EnrichedStringArray = (maxItem, maxItems) => z.object({
    value: z.array(z.string().min(1).max(maxItem)).max(maxItems).nullable(),
    confidence: z.number().min(0).max(1),
    sourceQuote: z.string().max(2000).nullable(),
    sourcePageUrl: z.string().url().max(2000).nullable(),
});
export const EnrichedFields = z.object({
    name: EnrichedString(300),
    type: z.object({
        value: ProviderType.nullable(),
        confidence: z.number().min(0).max(1),
        sourceQuote: z.string().max(2000).nullable(),
        sourcePageUrl: z.string().url().max(2000).nullable(),
    }),
    address: EnrichedString(500),
    city: EnrichedString(200),
    region: EnrichedString(200),
    province: EnrichedString(100),
    phone: EnrichedString(50),
    email: z.object({
        value: z.string().email().nullable(),
        confidence: z.number().min(0).max(1),
        sourceQuote: z.string().max(2000).nullable(),
        sourcePageUrl: z.string().url().max(2000).nullable(),
    }),
    website: z.object({
        value: z.string().url().nullable(),
        confidence: z.number().min(0).max(1),
        sourceQuote: z.string().max(2000).nullable(),
        sourcePageUrl: z.string().url().max(2000).nullable(),
    }),
    specialisations: EnrichedStringArray(100, 30),
    therapyTypes: EnrichedStringArray(100, 30),
    targetAudience: z.object({
        value: z.array(TargetAudience).min(1).max(3).nullable(),
        confidence: z.number().min(0).max(1),
        sourceQuote: z.string().max(2000).nullable(),
        sourcePageUrl: z.string().url().max(2000).nullable(),
    }),
    status: z.object({
        value: ProviderStatus.nullable(),
        confidence: z.number().min(0).max(1),
        sourceQuote: z.string().max(2000).nullable(),
        sourcePageUrl: z.string().url().max(2000).nullable(),
    }),
    waitingTime: EnrichedString(100),
    notes: EnrichedString(2000),
});
export const ProviderEnrichmentResult = z.object({
    providerId: z.string().min(1).max(120),
    websiteUrl: z.string().url(),
    pagesCrawled: z.array(z.string().url()).max(5),
    pagesSkipped: z
        .array(z.object({
        url: z.string().url(),
        reason: z.enum([
            'robots_disallowed',
            'timeout',
            'too_large',
            'fetch_failed',
            'budget_exceeded',
            'non_html',
            'blocked_url',
        ]),
    }))
        .max(20),
    fields: EnrichedFields,
    /** Wall-clock duration of the full preview pipeline. */
    durationMs: z.number().int().nonnegative(),
    /** ISO timestamp the preview was generated. */
    previewedAt: z.string().datetime(),
});
/**
 * Body of POST /providers/:id/apply-enrichment. Linda's "Accepteer" sends back
 * the previewed result verbatim — the apply route validates it against this
 * schema and writes only the non-null values. We do NOT re-extract on apply;
 * that would race against website edits and produce a different result than
 * what Linda saw in the diff.
 */
export const ApplyEnrichmentBody = z.object({
    preview: ProviderEnrichmentResult,
});
//# sourceMappingURL=provider-enrichment.js.map